import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { ErrorCode, resolveApiErrorContent } from '@app/routing/RouterToaster'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionEntity } from '@context/EntityContext'
import { useSnackbar } from '@context/SnackbarContext'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum, getTickmillPublicDomain } from '@utils/companyName.utils'

import { CreateLead, LeadMembersEnum } from '@model/CreateLead'
import { TickmillProductType } from '@model/TickmillProductType'

import { Loading } from '@shared/components/Loading/Loading'

import { trimTrackingParameter } from '../Referral/helpers'

export default function MarketingPage() {
  const { t } = useTranslation()

  const { addSnackbar } = useSnackbar()

  const apiClient = useApiClient(ClientApiClient)
  const [searchParams] = useSearchParams()

  const entity = useSessionEntity()
  const navigate = useNavigate()
  const [lead, setLead] = useState<CreateLead>()

  const prepareLead = () => {
    const lead = {
      source: searchParams.get(LeadMembersEnum.SOURCE) || '',
      typeId: searchParams.get(LeadMembersEnum.TYPE_ID) || '1',
      genderId: searchParams.get(LeadMembersEnum.GENDER_ID) || '0',
      firstName: searchParams.get(LeadMembersEnum.FIRST_NAME) || '',
      nativeName: searchParams.get(LeadMembersEnum.NATIVE_NAME) || '',
      lastName: searchParams.get(LeadMembersEnum.LAST_NAME) || '',
      middleName: searchParams.get(LeadMembersEnum.MIDDLE_NAME) || '',
      birthDay: searchParams.get(LeadMembersEnum.BIRTHDAY) || '',
      email: searchParams.get(LeadMembersEnum.EMAIL) || '',
      phoneNumber: searchParams.get(LeadMembersEnum.PHONE_NUMBER) || '',
      countryCode: searchParams.get(LeadMembersEnum.COUNTRY_CODE) || '',
      countryId: searchParams.get(LeadMembersEnum.COUNTRY_ID) || '',
      languageId: searchParams.get(LeadMembersEnum.LANGUAGE_ID) || 'en',
      optIn: false,
      campaign: trimTrackingParameter(searchParams.get(LeadMembersEnum.CAMPAIGN) || ''),
      campaignSource: trimTrackingParameter(
        searchParams.get(LeadMembersEnum.CAMPAIGN_SOURCE) || ''
      ),
      campaignMedium: trimTrackingParameter(
        searchParams.get(LeadMembersEnum.CAMPAIGN_MEDIUM) || ''
      ),
      campaignTerm: trimTrackingParameter(searchParams.get(LeadMembersEnum.CAMPAIGN_TERM) || ''),
      campaignContent: trimTrackingParameter(
        searchParams.get(LeadMembersEnum.CAMPAIGN_CONTENT) || ''
      ),
      referrer: searchParams.get(LeadMembersEnum.REFERRER) || '',
      referralCode: searchParams.get(LeadMembersEnum.REFERRER_CODE) || '',
      linkClicked: searchParams.get(LeadMembersEnum.LINK_CLICKED) || new Date().toISOString(),
      tickmillCompanyId: Number(searchParams.get(LeadMembersEnum.TICKMILL_COMPANY_ID)) || entity,
      tickmillPublicDomain: getTickmillPublicDomain(),
      productIds:
        entity === TickmillCompaniesEnum.TICKMILL_UK
          ? [TickmillProductType.CFD, TickmillProductType.ETD]
          : [TickmillProductType.CFD],
      agreedLegalDocuments: [],
    }
    setLead(lead)

    return lead
  }

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.marketing.lead({}),
    queryFn: async () => {
      try {
        const res = await apiClient.createLead(prepareLead())
        return res
      } catch (err: unknown) {
        const error = err as ResponseError
        const code = error?.response?.response?.data?.code

        const errorDisplayContent = resolveApiErrorContent(code as ErrorCode, t)

        if (!errorDisplayContent.title) {
          addSnackbar.error({ message: t('Something went wrong! Please try again later') })
        }
      }
    },
  })

  if (data) {
    const params = new URLSearchParams()
    lead && Object.entries(lead).map(([k, v]) => params.set(k.toString(), v.toString()))
    navigate(`/sign-up?${params.toString()}`)
  }

  return <Loading isLoading={isLoading} showLoadingIcon text={t('Creating lead')} />
}
